import type { i18n as I18nInstance, TFunction } from 'i18next';

import { isolate } from '@/utils/bidi';
import type { SuggestedPrompt } from '@/components/chat/ChatLandingState';

/**
 * One ranked prompt as the backend sends it (`GET /api/prompts/suggested`).
 *
 * A prompt carries **either** `key` + `params` — a template this client
 * renders from its own locale files — **or** `label` + `prompt`, text the
 * backend already produced in the requested locale. The one exception is the
 * ballot-builder chip, which pairs a translated `key` label with a literal
 * `prompt`: that message opens a flow by exact phrase, so it is matched rather
 * than read.
 *
 * Mirrors `votemate-mobile-app/src/constants/smartPrompts.ts` — same contract,
 * same rendering rules, same skip behavior.
 */
export interface SmartPromptItem {
  id: string;
  category: SuggestedPrompt['category'];
  source: 'catalog';
  score: number;
  key?: string;
  params?: Record<string, string | number | null>;
  label?: string;
  prompt?: string;
}

export interface SmartPromptsResponse {
  v: number;
  prompts: SmartPromptItem[];
  signals?: Record<string, unknown>;
}

const CHAT_NAMESPACE = 'chat';

/** Categories this client knows how to render; anything else is dropped. */
const KNOWN_CATEGORIES = new Set(['general', 'race', 'measure', 'transparency']);

/**
 * Parameters holding a proper noun off the ballot — an election, a contest, a
 * measure, a party, an issue label. Each is interpolated into translated prose
 * and is routinely Latin script regardless of the locale, so each is bidi
 * isolated before it goes into the copy, the way the static chip sets already
 * isolate an election name. Applied unconditionally rather than behind a
 * direction check: FSI/PDI are zero-width and inert in an LTR paragraph, so
 * one code path covers all 16 locales.
 */
const NAME_PARAMS = new Set([
  'electionName',
  'displayTitle',
  'measureTitle',
  'party',
  'topicLabel',
]);

/**
 * Pin the Gregorian calendar for date formatting.
 *
 * Persian defaults to the Solar Hijri calendar, which would render an election
 * date as a different date in a different era — unreconcilable against a
 * voter's polling notice. The mobile app pins this in `utils/dateLocale.ts`;
 * pinning it here keeps a chip reading the same on both clients.
 */
const GREGORIAN = '-u-ca-gregory';

function prettyDate(iso: string, language: string): string | undefined {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return undefined;
  try {
    return new Intl.DateTimeFormat(`${language}${GREGORIAN}`, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(parsed);
  } catch {
    return undefined;
  }
}

function daysText(days: number, t: TFunction): string {
  if (days === 0) return t('landing.prompts.dynamic.daysText.today');
  if (days === 1) return t('landing.prompts.dynamic.daysText.tomorrow');
  return t('landing.prompts.dynamic.daysText.inDays', { days });
}

/**
 * Expand the backend's raw parameters into what the copy interpolates.
 *
 * The backend sends facts (`days: 12`, `electionDate: '2026-11-03'`); the
 * phrasings built from them (`daysText`, `prettyDate`) are language-specific,
 * so they are derived here where the locale files live.
 */
export function buildRenderParams(
  params: SmartPromptItem['params'],
  t: TFunction,
  language: string
): Record<string, string | number> {
  const rendered: Record<string, string | number> = {};
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    rendered[key] = NAME_PARAMS.has(key) && typeof value === 'string' ? isolate(value) : value;
  });

  if (typeof rendered.days === 'number') {
    rendered.daysText = daysText(rendered.days, t);
  }
  if (typeof rendered.electionDate === 'string') {
    const formatted = prettyDate(rendered.electionDate, language);
    if (formatted) rendered.prettyDate = formatted;
  }
  if (typeof rendered.registrationDeadline === 'string') {
    const formatted = prettyDate(rendered.registrationDeadline, language);
    if (formatted) rendered.deadlineDate = formatted;
  }
  if (typeof rendered.earlyVotingStart === 'string') {
    const formatted = prettyDate(rendered.earlyVotingStart, language);
    if (formatted) rendered.earlyVotingDate = formatted;
  }
  return rendered;
}

/**
 * Render one backend item into a chip, or return `null` to skip it.
 *
 * Skipping is the designed behavior for a `key` this build does not ship: the
 * backend can add a template before a client deploy carries its copy, and the
 * voter sees the next-ranked prompt instead of an untranslated or blank chip.
 */
export function renderSmartPrompt(
  item: SmartPromptItem,
  t: TFunction,
  i18n: I18nInstance
): SuggestedPrompt | null {
  if (!KNOWN_CATEGORIES.has(item.category)) return null;

  // Every prompt is templated; `prompt` is set only for the ballot-builder
  // literal, which is matched rather than read.
  if (!item.key) return null;

  const labelKey = `${item.key}.label`;
  if (!i18n.exists(labelKey, { ns: CHAT_NAMESPACE })) return null;

  const promptKey = `${item.key}.prompt`;
  const hasPromptCopy = i18n.exists(promptKey, { ns: CHAT_NAMESPACE });
  if (!item.prompt && !hasPromptCopy) return null;

  const params = buildRenderParams(item.params, t, i18n.language);
  const label = t(labelKey, params);
  // A literal prompt from the backend wins: it is a phrase to be matched, not
  // copy to be translated.
  const prompt = item.prompt ?? t(promptKey, params);
  if (!label || !prompt) return null;

  return { label, prompt, category: item.category, id: item.id, source: item.source };
}

/** Render a whole response, dropping items this build cannot show. */
export function renderSmartPrompts(
  items: SmartPromptItem[] | undefined,
  t: TFunction,
  i18n: I18nInstance
): SuggestedPrompt[] {
  return (items ?? [])
    .map((item) => renderSmartPrompt(item, t, i18n))
    .filter((prompt): prompt is SuggestedPrompt => prompt !== null);
}

/**
 * A stable key for the parts of a client context that change the ranking.
 *
 * The clients rebuild their conversation context object freely, so keying a
 * fetch on its identity would refetch on every render. What actually moves the
 * ranking is which election the context describes and how much is on it, so
 * that is what this summarizes. Returns an empty string for no context.
 */
export function describeClientContext(context: unknown): string {
  if (!context || typeof context !== 'object') return '';
  const election = (context as { election?: unknown }).election;
  const block = (election && typeof election === 'object' ? election : context) as {
    elections?: Array<{
      election_date?: string;
      contests?: unknown[];
      ballot_measures?: unknown[];
    }>;
  };
  const first = block.elections?.[0];
  if (!first) return 'ctx:empty';
  return [
    'ctx',
    first.election_date ?? '',
    first.contests?.length ?? 0,
    first.ballot_measures?.length ?? 0,
    block.elections?.length ?? 0,
  ].join(':');
}
